import { formatDistanceToNow } from "date-fns";
import { CopyButton } from "@/components/animate-ui/components/buttons/copy";
import { CircleCheck } from "@/components/animate-ui/icons/circle-check";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import type { components } from "@/lib/api/types";

type QuickCreateResponse = components["schemas"]["QuickCreateTaskResponse"];

interface TaskCreationHeroProps {
	isAuthenticated: boolean;
	heading: string;
}

export function TaskCreationHero({ isAuthenticated, heading }: TaskCreationHeroProps) {
	if (isAuthenticated) {
		return (
			<div className="space-y-2.5">
				<h1 className="max-w-2xl text-balance text-3xl font-semibold tracking-tight sm:text-[2rem] lg:text-[2.25rem]">
					{heading}
				</h1>
			</div>
		);
	}

	return (
		<div className="space-y-2.5 sm:space-y-3">
			<h1 className="max-w-2xl text-balance text-3xl font-semibold tracking-tight sm:text-[2.6rem] lg:text-[3.2rem]">
				AI teammates that actually do your work.
			</h1>
			<p className="max-w-xl text-sm leading-relaxed text-muted-foreground sm:max-w-2xl sm:text-[0.95rem] lg:text-base">
				Answers questions, gives advice, and goes off to solve tasks. Connected to your company
				knowledge and 70+ app integrations. Start automating operations with measurable ROI.
			</p>
		</div>
	);
}

export function UnauthenticatedHowItWorks() {
	return (
		<div className="space-y-4 sm:space-y-5">
			<h2 className="max-w-2xl text-balance text-xl font-semibold tracking-tight sm:text-2xl">
				Automate operations across the organization
			</h2>
			<div className="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4">
				<div className="surface-glass-interactive surface-divider rounded-xl border p-4 sm:p-5">
					<p className="text-sm font-semibold">1. Access company knowledge</p>
					<p className="text-sm text-muted-foreground">
						Connect 70+ app integrations for context and action.
					</p>
				</div>
				<div className="surface-glass-interactive surface-divider rounded-xl border p-4 sm:p-5">
					<p className="text-sm font-semibold">2. Automate what drives ROI</p>
					<p className="text-sm text-muted-foreground">
						Assign tasks on a schedule, through integrated apps, or by sending an email.
					</p>
				</div>
				<div className="surface-glass-interactive surface-divider rounded-xl border p-4 sm:p-5">
					<p className="text-sm font-semibold">3. Work gets done</p>
					<p className="text-sm text-muted-foreground">
						Your teammate executes autonomously and delivers real, measurable results.
					</p>
				</div>
			</div>
		</div>
	);
}

interface ScheduledTaskSuccessDialogProps {
	successData: QuickCreateResponse | null;
	onOpenChange: (open: boolean) => void;
	onSeeTeammate: () => void;
	onRunNow: () => void;
	onCreateAnother: () => void;
}

export function ScheduledTaskSuccessDialog({
	successData,
	onOpenChange,
	onSeeTeammate,
	onRunNow,
	onCreateAnother,
}: ScheduledTaskSuccessDialogProps) {
	return (
		<Dialog open={!!successData} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-sm">
				<DialogHeader className="items-center text-center">
					<CircleCheck className="mb-2 size-10 text-accent-cyan" />
					<DialogTitle>Task scheduled</DialogTitle>
					<DialogDescription>
						{successData?.schedule?.next_run_time
							? `First run ${formatDistanceToNow(new Date(successData.schedule.next_run_time), { addSuffix: true }).replace("about ", "")}`
							: "Your task has been scheduled."}
					</DialogDescription>
				</DialogHeader>
				<DialogFooter className="flex-col gap-2 sm:flex-col">
					<Button onClick={onSeeTeammate}>See teammate</Button>
					<Button variant="outline" onClick={onRunNow}>
						Run now
					</Button>
					<Button variant="ghost" onClick={onCreateAnother}>
						Create another task
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

interface WebhookUrlDialogProps {
	webhookUrl: string | null;
	onOpenChange: (open: boolean) => void;
}

export function WebhookUrlDialog({ webhookUrl, onOpenChange }: WebhookUrlDialogProps) {
	return (
		<Dialog open={!!webhookUrl} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-xl">
				<DialogHeader>
					<DialogTitle>Webhook URL Created</DialogTitle>
					<DialogDescription>
						Make a POST request to this URL with an optional JSON payload to trigger this task. The
						full URL is shown only once. Copy it now.
					</DialogDescription>
				</DialogHeader>
				{webhookUrl && (
					<div className="rounded-md border bg-muted/40 p-3 flex items-start justify-between gap-3">
						<code className="text-xs break-all">{webhookUrl}</code>
						<CopyButton content={webhookUrl} variant="outline" size="sm" />
					</div>
				)}
				<DialogFooter>
					<Button type="button" onClick={() => onOpenChange(false)}>
						Close
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
